<template>
  <div class="space-y-8">
    <!-- Статистика -->
    <div class="grid grid-cols-4 gap-6">
      <!-- Всего заказов -->
      <div class="bg-white p-6 rounded-lg shadow">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-gray-600 text-sm">Всего заказов</p>
            <p class="text-4xl font-bold">{{ stats.totalOrders }}</p>
          </div>
          <div class="text-4xl">📦</div>
        </div>
        <p class="text-xs text-green-600 mt-2">↑ {{ stats.ordersThisMonth }} этот месяц</p>
      </div>

      <!-- Доход -->
      <div class="bg-white p-6 rounded-lg shadow">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-gray-600 text-sm">Всего доход</p>
            <p class="text-4xl font-bold">₴{{ stats.totalRevenue }}</p>
          </div>
          <div class="text-4xl">💰</div>
        </div>
        <p class="text-xs text-green-600 mt-2">↑ ₴{{ stats.revenueThisMonth }} этот месяц</p>
      </div>

      <!-- Активных пользователей -->
      <div class="bg-white p-6 rounded-lg shadow">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-gray-600 text-sm">Активные пользователи</p>
            <p class="text-4xl font-bold">{{ stats.activeUsers }}</p>
          </div>
          <div class="text-4xl">👥</div>
        </div>
        <p class="text-xs text-blue-600 mt-2">{{ stats.newUsersThisMonth }} новых этот месяц</p>
      </div>

      <!-- Услуг доступно -->
      <div class="bg-white p-6 rounded-lg shadow">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-gray-600 text-sm">Услуг доступно</p>
            <p class="text-4xl font-bold">{{ stats.totalServices }}</p>
          </div>
          <div class="text-4xl">🔧</div>
        </div>
        <p class="text-xs text-purple-600 mt-2">{{ stats.activeServices }} активных</p>
      </div>
    </div>

    <!-- Недавние заказы -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-6 border-b flex justify-between items-center">
        <h2 class="text-xl font-bold">📦 Недавние заказы</h2>
        <router-link to="/admin/orders" class="text-blue-600 hover:underline">
          Просмотреть все →
        </router-link>
      </div>

      <div v-if="loading" class="p-6 text-center text-gray-600">
        Загрузка...
      </div>

      <div v-else-if="recentOrders.length === 0" class="p-6 text-center text-gray-600">
        Нет заказов
      </div>

      <table v-else class="w-full">
        <thead class="bg-gray-50 border-b">
          <tr>
            <th class="px-6 py-3 text-left text-sm font-bold">№ Заказа</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Пользователь</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Сумма</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Статус</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Платеж</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Дата</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Действие</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="order in recentOrders" :key="order._id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-bold">{{ order.orderNumber }}</td>
            <td class="px-6 py-4">
              <div>
                <p class="font-bold">{{ order.user?.name }}</p>
                <p class="text-sm text-gray-600">{{ order.user?.email }}</p>
              </div>
            </td>
            <td class="px-6 py-4 font-bold">₴{{ order.totalPrice }}</td>
            <td class="px-6 py-4">
              <span :class="getStatusClass(order.status)" class="px-3 py-1 rounded-full text-sm font-bold">
                {{ getStatusLabel(order.status) }}
              </span>
            </td>
            <td class="px-6 py-4">
              <span :class="getPaymentClass(order.paymentStatus)" class="px-3 py-1 rounded-full text-sm font-bold">
                {{ getPaymentLabel(order.paymentStatus) }}
              </span>
            </td>
            <td class="px-6 py-4 text-sm text-gray-600">
              {{ formatDate(order.createdAt) }}
            </td>
            <td class="px-6 py-4">
              <router-link
                :to="{ name: 'AdminOrderDetail', params: { id: order._id } }"
                class="text-blue-600 hover:underline text-sm"
              >
                Подробнее
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Графики (простые) -->
    <div class="grid grid-cols-2 gap-6">
      <!-- Заказы по статусам -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-bold mb-4">Статусы заказов</h3>
        <div class="space-y-3">
          <div v-for="(count, status) in ordersByStatus" :key="status" class="flex justify-between items-center">
            <span class="text-gray-600">{{ getStatusLabel(status) }}</span>
            <div class="flex items-center gap-2">
              <div class="w-32 bg-gray-200 rounded-full h-2">
                <div
                  :style="{ width: (count / totalOrders * 100) + '%' }"
                  :class="getStatusBarClass(status)"
                  class="h-2 rounded-full"
                ></div>
              </div>
              <span class="font-bold w-8 text-right">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Доход по месяцам -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h3 class="text-lg font-bold mb-4">Доход (последние 6 месяцев)</h3>
        <div class="space-y-3">
          <div v-for="(revenue, month) in revenueByMonth" :key="month" class="flex justify-between items-center">
            <span class="text-gray-600">{{ month }}</span>
            <div class="flex items-center gap-2">
              <div class="w-32 bg-gray-200 rounded-full h-2">
                <div
                  :style="{ width: (revenue / maxRevenue * 100) + '%' }"
                  class="h-2 rounded-full bg-green-500"
                ></div>
              </div>
              <span class="font-bold w-12 text-right">₴{{ revenue }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
const useAuthStore = () => ({ user: { name: 'Admin' }, isAuthenticated: true, logout: () => {} });
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { uk } from 'date-fns/locale';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'AdminDashboard',
  setup() {
    const authStore = useAuthStore();
    const loading = ref(false);
    const recentOrders = ref([]);

    const stats = ref({
      totalOrders: 0,
      totalRevenue: 0,
      ordersThisMonth: 0,
      revenueThisMonth: 0,
      activeUsers: 0,
      newUsersThisMonth: 0,
      totalServices: 0,
      activeServices: 0
    });

    const ordersByStatus = ref({
      pending: 0,
      accepted: 0,
      'in-progress': 0,
      completed: 0,
      cancelled: 0
    });

    const revenueByMonth = ref({
      'Янв': 5000,
      'Февр': 6500,
      'Март': 7200,
      'Апр': 8100,
      'Май': 7800,
      'Июнь': 9200
    });

    const maxRevenue = computed(() => {
      return Math.max(...Object.values(revenueByMonth.value));
    });

    const totalOrders = computed(() => {
      return Object.values(ordersByStatus.value).reduce((a, b) => a + b, 0);
    });

    const statusLabels = {
      pending: 'Ожидание',
      accepted: 'Принят',
      'in-progress': 'В работе',
      completed: 'Завершен',
      cancelled: 'Отменен'
    };

    const paymentStatusLabels = {
      unpaid: 'Не оплачен',
      paid: 'Оплачен',
      partial: 'Частично'
    };

    const loadDashboard = async () => {
      loading.value = true;
      try {
        // Получаем заказы
        const ordersResponse = await axios.get(
          `${API_URL}/orders`,
          {
            headers: {
              Authorization: `Bearer ${authStore.token}`
            }
          }
        );

        const orders = ordersResponse.data;
        recentOrders.value = orders.slice(0, 10);

        // Подсчитываем статистику
        stats.value.totalOrders = orders.length;
        stats.value.totalRevenue = orders.reduce((sum, o) => sum + o.totalPrice, 0);

        // Подсчитываем по статусам
        orders.forEach(order => {
          if (ordersByStatus.value.hasOwnProperty(order.status)) {
            ordersByStatus.value[order.status]++;
          }
        });

        // TODO: Получить реальные данные пользователей и услуг
        stats.value.activeUsers = 45;
        stats.value.newUsersThisMonth = 8;
        stats.value.totalServices = 12;
        stats.value.activeServices = 11;

        // Заказы этого месяца
        const thisMonth = new Date();
        thisMonth.setDate(1);
        stats.value.ordersThisMonth = orders.filter(
          o => new Date(o.createdAt) >= thisMonth
        ).length;

        stats.value.revenueThisMonth = orders
          .filter(o => new Date(o.createdAt) >= thisMonth)
          .reduce((sum, o) => sum + o.totalPrice, 0);

      } catch (error) {
        console.error('Ошибка при загрузке dashboard:', error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (date) => {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: uk });
    };

    const getStatusLabel = (status) => statusLabels[status] || status;
    const getStatusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-700',
        accepted: 'bg-blue-100 text-blue-700',
        'in-progress': 'bg-purple-100 text-purple-700',
        completed: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    };

    const getStatusBarClass = (status) => {
      const classes = {
        pending: 'bg-yellow-500',
        accepted: 'bg-blue-500',
        'in-progress': 'bg-purple-500',
        completed: 'bg-green-500',
        cancelled: 'bg-red-500'
      };
      return classes[status] || 'bg-gray-500';
    };

    const getPaymentLabel = (status) => paymentStatusLabels[status] || status;
    const getPaymentClass = (status) => {
      const classes = {
        unpaid: 'bg-red-100 text-red-700',
        paid: 'bg-green-100 text-green-700',
        partial: 'bg-yellow-100 text-yellow-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    };

    onMounted(() => {
      loadDashboard();
    });

    return {
      loading,
      recentOrders,
      stats,
      ordersByStatus,
      revenueByMonth,
      totalOrders,
      maxRevenue,
      formatDate,
      getStatusLabel,
      getStatusClass,
      getStatusBarClass,
      getPaymentLabel,
      getPaymentClass
    };
  }
};
</script>

